import React, { useEffect, useState } from 'react';
import { Login } from '@milkmoovement/common_cognito_package';
import { onCognitoLogin } from '../../../auth/store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getRegion, getHostname } from '../../../../utils';
import { showMessage } from '../../../store/actions';
import LegacyIntercomButton from '../../../custom-components/LegacyIntercomButton';
import { cacheAuthenticatedRegion, fetchAuthConfigFromBackend } from '../../../../utils/regionConfig';

const LoginPage = () => {
    const history = useHistory();
    const dispatch = useDispatch();

    const hostname = getHostname(window);
    let authConfig;

    const [internalSSO, setInternalSSO] = useState(false);
    const [region, setRegion] = useState(() => sessionStorage.getItem('mm.auth.region'));
    const [coreSystem, setCoreSystem] = useState(() => sessionStorage.getItem('mm.auth.coreSystem'));
    const [isLoadingRegion, setIsLoadingRegion] = useState(() => {
        return !(sessionStorage.getItem('mm.auth.region') && sessionStorage.getItem('mm.auth.coreSystem'));
    });

    const providersMapJSON = process.env.REACT_APP_PROVIDERS_MAP;
    const providersMap = providersMapJSON ? JSON.parse(providersMapJSON) : {};
    const internalProviderJSON = process.env.REACT_APP_INTERNAL_PROVIDER_MAP;
    const internalProvider = internalProviderJSON ? JSON.parse(internalProviderJSON) : {};

    // All hooks must be called before any conditional returns
    const { error } = useSelector(({ persisted }) => persisted.auth.login);

    // Fetch region from backend on component mount
    useEffect(() => {
        const loadRegion = async () => {
            try {
                const authMetadata = await fetchAuthConfigFromBackend();
                setRegion(authMetadata.region);
                setCoreSystem(authMetadata.coreSystem);
            } catch (err) {
                // Fallback to URL-based region detection
                const fallbackRegion = getRegion(window.location.hostname);
                setRegion(fallbackRegion);
                setCoreSystem(fallbackRegion);
            } finally {
                setIsLoadingRegion(false);
            }
        };

        if (!(sessionStorage.getItem('mm.auth.region') && sessionStorage.getItem('mm.auth.coreSystem'))) {
            loadRegion();
        }
    }, []);

    // Internal redirection based on cookie set in Cloudfront function
    useEffect(() => {
        const cookie = document.cookie.split('; ').find((row) => row.startsWith('internal-redirect='));
        const redirectValue = cookie && internalProviderJSON ? cookie.split('=')[1] : null;

        if (redirectValue === 'true') {
            // Delete cookie so redirection only happens once in the session
            document.cookie = 'internal-redirect=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            setInternalSSO(true);
            localStorage.setItem('mm.login.internalLogin', 'true');
        }
    }, [internalProviderJSON]);

    // Handle error messages
    useEffect(() => {
        if (error.message && error.name === 'RoleError') {
            dispatch(showMessage({ message: error.message }));
        }
    }, [error, dispatch]);

    // Wait for region to be loaded before rendering
    if (isLoadingRegion || !region || !coreSystem) {
        return null; // Return null instead of loading UI to avoid flash
    }

    const providers = Object.hasOwn(providersMap, region) ? providersMap[region] : [];
    const cookie = document.cookie.split('; ').find((row) => row.startsWith('internal-redirect='));
    const redirectValue = cookie && internalProviderJSON ? cookie.split('=')[1] : null;

    if (localStorage.getItem('mm.login.internalLogin') === 'true' || redirectValue === 'true') {
        authConfig = {
            domain: process.env.REACT_APP_OAUTH_DOMAIN,
            redirectSignIn: hostname,
            redirectSignOut: hostname,
            clientId: internalProvider.clientId,
        };
        providers.push(internalProvider);
    } else if (providers?.length > 0) {
        authConfig = {
            domain: process.env.REACT_APP_OAUTH_DOMAIN,
            redirectSignIn: hostname,
            redirectSignOut: hostname,
            clientId: providers[0].clientId, // all providers currently have the same client id
        };
    }

    const handleNavigateForgotPassword = () => history.push({ pathname: '/forgot-password' });

    const handleSucessfulLogin = async () => {
        cacheAuthenticatedRegion(region);
        dispatch(await onCognitoLogin());
    };
    const handleNavigateLogin = () => setInternalSSO(false);

    return (
        <>
            <Login
                onSucessfulLogin={handleSucessfulLogin}
                onNavigateForgotPassword={handleNavigateForgotPassword}
                onNavigateLogin={handleNavigateLogin}
                metadata={{
                    coreSystem,
                }}
                internalLoginRender={internalSSO}
                authConfig={authConfig}
                providers={providers}
            />
            <LegacyIntercomButton />
        </>
    );
};

export default LoginPage;
